import type { Action } from './action'
import type { ActivityRecord } from './history'

export type ObjectiveStatus = 'active' | 'later' | 'completed' | 'archived'

export interface MissionNote {
  id: string
  text: string
  nextStep: string
  actionId?: string | null
  createdAt: number
}

export interface CheckpointAction {
  actionId?: string | null
  newActionName?: string
}

export interface Objective {
  id: string
  areaId: string
  name: string
  status: ObjectiveStatus
  priority: number
  createdAt: number
  updatedAt: number
  deadline?: string | null
  originalDeadline?: string | null
  raceStartedAt?: number
  notes?: MissionNote[]
  closedProgress?: { completed: number; total: number }
}

export interface ObjectiveDraft {
  name: string
  activate: boolean
  deadline?: string | null
}

// Calendar dates stay local: parsing YYYY-MM-DD as UTC shifts the deadline in the Americas.
export const isDeadline = (value: unknown): value is string => {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false
  const date = new Date(`${value}T12:00:00`)
  return Number.isFinite(date.getTime()) && localDate(date) === value
}

export const localDate = (date = new Date()) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
export const deadlineEnd = (date: string) => new Date(`${date}T23:59:59.999`).getTime()
const calendarDay = (timestamp: number) => {
  const d = new Date(timestamp)
  return Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / 86_400_000
}

export const missionRace = (objective: Objective, actions: Action[], now = Date.now(), records: ActivityRecord[] = []) => {
  const linked = actions.filter((action) => action.areaId === objective.areaId && action.objectiveIds.includes(objective.id))
  const steps = linked.filter((action) => action.repeatMode === 'once')
  const snapshot = objective.status === 'completed' || objective.status === 'archived' ? objective.closedProgress : undefined
  const completed = snapshot?.completed ?? steps.filter((action) => action.completedAt !== null).length
  const total = snapshot?.total ?? steps.length
  const start = objective.raceStartedAt ?? objective.createdAt
  const end = objective.deadline && isDeadline(objective.deadline) ? deadlineEnd(objective.deadline) : null
  return {
    steps,
    completed,
    progress: total ? completed / total : 0,
    time: end === null ? 0 : Math.max(0, Math.min(1, (now - start) / Math.max(1, end - start))),
    daysLeft: end === null ? null : calendarDay(end) - calendarDay(now),
    lastProgressAt: records.filter((record) => record.objectiveIds ? record.objectiveIds.includes(objective.id) : linked.some((action) => action.id === record.actionId)).reduce<number | null>((last, record) => Math.max(last ?? 0, record.completedAt), null),
  }
}
